import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  DebtsOverNetWorthLimitsDto,
  FindingLevel,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import { formatRatio } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';

type BandKey = 'ok' | 'neutral' | 'atencao' | 'critico';

/** Faixa do nível que o backend deu ao ponto de dívidas (sem nível = entre OK e Atenção, faixa neutra). */
function bandOfLevel(level: FindingLevel | null): BandKey {
  if (level === 'critico' || level === 'atencao' || level === 'ok') return level;
  return 'neutral';
}

/**
 * Barra dívidas ÷ PL dos KPIs do sócio e do grupo. Só desenho: o valor, os limites (`debtsOverNetWorthLimits`, em
 * fração) e o nível vêm do backend. A faixa do nível atual fica cheia e as outras claras; a escala vai até 1,5 vez o
 * limite de Crítico (sem limites, até 100% do PL) e, acima dela, o marcador fica no fim e o número ao lado diz o resto.
 */
@Component({
  selector: 'app-pit-debt-bar',
  standalone: true,
  templateUrl: './debt-bar.component.html',
  styleUrl: './debt-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DebtBarComponent {
  /** Dívidas ÷ PL, em fração. */
  readonly ratio = input<number | null>(null);
  readonly limits = input<DebtsOverNetWorthLimitsDto | null | undefined>(null);
  readonly level = input<FindingLevel | null>(null);
  /** "do patrimônio" ou "do patrimônio somado", para o leitor de tela. */
  readonly reference = input('do patrimônio');

  protected readonly bar = computed(() => {
    const value = this.ratio();
    if (value == null || !Number.isFinite(value)) return null;
    const limits = this.limits() ?? null;
    const scale = limits ? Math.max(limits.critical * 1.5, limits.critical + 0.1) : 1;
    const pct = (n: number) => Math.min(100, Math.max(0, (n / scale) * 100));
    const current = bandOfLevel(this.level());
    const bands = limits
      ? (
          [
            ['ok', pct(limits.ok)],
            ['neutral', pct(limits.attention) - pct(limits.ok)],
            ['atencao', pct(limits.critical) - pct(limits.attention)],
            ['critico', 100 - pct(limits.critical)],
          ] as [BandKey, number][]
        ).map(([key, width]) => ({ key, width, current: key === current }))
      : [];
    // Legenda curta, numa linha: "OK < 10% · Atenção ≥ 30% · Crítico > 60%".
    const legend = limits
      ? `OK < ${formatRatio(limits.ok)} · Atenção ≥ ${formatRatio(limits.attention)} · Crítico > ${formatRatio(limits.critical)}`
      : '';
    return {
      marker: pct(value),
      overflow: value > scale,
      bands,
      level: this.level(),
      legend,
      label: `Dívidas em ${formatRatio(value)} ${this.reference()}${legend ? `. ${legend}` : ''}`,
    };
  });
}
